import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  promptSuggestionsService,
} from '../services/promptSuggestionsService';
import { useLocalization } from './LocalizationContext';
import { useSession } from './SessionContext';
import type {
  PromptSuggestionCatalog,
  PromptSuggestionCategory,
  PromptSuggestionItem,
  PromptSuggestionsErrorInfo,
  PromptSuggestionSurface,
} from '../types/promptSuggestions';

export type PromptSuggestionsStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

export interface PromptSuggestionsContextValue {
  catalogVersion: string | null;
  categories: PromptSuggestionCategory[];
  contentDigest: string | null;
  suggestionsForSurface: (surface: PromptSuggestionSurface) => PromptSuggestionItem[];
  isLoading: boolean;
  status: PromptSuggestionsStatus;
  error: PromptSuggestionsErrorInfo | null;
  reload: () => Promise<void>;
}

interface PromptSuggestionsCacheEntry {
  catalog: PromptSuggestionCatalog;
  etag: string | null;
}

interface PromptSuggestionsScope {
  cacheKey: string;
  key: string;
  locale: string;
  requestKey: string;
  sessionId: string;
}

interface PromptSuggestionsLoadRequest {
  controller: AbortController;
  promise: Promise<void>;
}

type PromptSuggestionsCache = Record<string, PromptSuggestionsCacheEntry>;
type PromptSuggestionsErrorCache = Record<string, PromptSuggestionsErrorInfo | undefined>;
type PromptSuggestionsBySurface = Partial<
  Record<PromptSuggestionSurface, PromptSuggestionItem[]>
>;

const PromptSuggestionsContext = createContext<PromptSuggestionsContextValue | undefined>(
  undefined,
);

const isShuffledSurface = (surface: PromptSuggestionSurface): boolean => (
  surface === 'home' || surface === 'composer' || surface === 'right_panel'
);

const createScope = (
  sessionId: string | null,
  customerId: string | null,
  locale: string,
  isLocaleReady: boolean,
): PromptSuggestionsScope | null => {
  if (!sessionId || !customerId || !isLocaleReady) {
    return null;
  }

  return {
    cacheKey: JSON.stringify([customerId, locale]),
    key: JSON.stringify([sessionId, customerId, locale]),
    locale,
    requestKey: JSON.stringify([sessionId, customerId, locale]),
    sessionId,
  };
};

const toErrorInfo = (error: unknown): PromptSuggestionsErrorInfo => ({
  code: 'prompt_suggestions_load_failed',
  detail: error instanceof Error && error.message ? error.message : null,
});

const isAbortError = (error: unknown): boolean => (
  error instanceof DOMException && error.name === 'AbortError'
);

const getCurrentDay = (): string => new Date().toISOString().slice(0, 10);

const hashSeed = (seed: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const createSeededRandom = (seed: string): (() => number) => {
  let state = hashSeed(seed);
  return () => {
    state += 0x6D2B79F5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
};

const shuffleWithinRank = <Item,>(items: Item[], seed: string): Item[] => {
  const shuffledItems = [...items];
  const random = createSeededRandom(seed);
  for (let index = shuffledItems.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [shuffledItems[index], shuffledItems[swapIndex]] = [
      shuffledItems[swapIndex],
      shuffledItems[index],
    ];
  }
  return shuffledItems;
};

const sortStableByPlacement = (items: PromptSuggestionItem[]): PromptSuggestionItem[] => (
  [...items].sort((left, right) => (
    left.placement.rank - right.placement.rank
    || left.id.localeCompare(right.id)
    || left.placement.variant.localeCompare(right.placement.variant)
  ))
);

const orderSurfaceItems = (
  surface: PromptSuggestionSurface,
  items: PromptSuggestionItem[],
  sessionId: string,
  catalogVersion: string,
  day: string,
): PromptSuggestionItem[] => {
  const itemsByRank = new Map<number, PromptSuggestionItem[]>();
  for (const item of sortStableByPlacement(items)) {
    const rankItems = itemsByRank.get(item.placement.rank) || [];
    rankItems.push(item);
    itemsByRank.set(item.placement.rank, rankItems);
  }

  return [...itemsByRank.entries()]
    .sort(([leftRank], [rightRank]) => leftRank - rightRank)
    .flatMap(([rank, rankItems]) => (
      isShuffledSurface(surface)
        ? shuffleWithinRank(
          rankItems,
          `${sessionId}:${catalogVersion}:${surface}:${day}:${rank}`,
        )
        : rankItems
    ));
};

const buildSuggestionsBySurface = (
  catalog: PromptSuggestionCatalog,
  sessionId: string,
  day: string,
): PromptSuggestionsBySurface => {
  const surfaceItems: PromptSuggestionsBySurface = {};
  for (const suggestion of catalog.suggestions) {
    for (const placement of suggestion.placements) {
      const items = surfaceItems[placement.surface] || [];
      items.push({
        ...suggestion,
        placement,
      });
      surfaceItems[placement.surface] = items;
    }
  }

  return Object.fromEntries(
    Object.entries(surfaceItems).map(([surface, items]) => [
      surface,
      orderSurfaceItems(
        surface as PromptSuggestionSurface,
        items,
        sessionId,
        catalog.catalog_version,
        day,
      ),
    ]),
  ) as PromptSuggestionsBySurface;
};

export const PromptSuggestionsProvider = ({ children }: { children: ReactNode }) => {
  const { session } = useSession();
  const { effectiveLocale, isLocaleReady } = useLocalization();
  const sessionId = session?.sessionId || null;
  const customerId = session?.customerId || null;
  const scope = createScope(sessionId, customerId, effectiveLocale, isLocaleReady);
  const scopeKey = scope?.key || null;
  const [catalogCache, setCatalogCache] = useState<PromptSuggestionsCache>({});
  const [errorCache, setErrorCache] = useState<PromptSuggestionsErrorCache>({});
  const [loadingRequestKey, setLoadingRequestKey] = useState<string | null>(null);
  const activeScopeRef = useRef<PromptSuggestionsScope | null>(scope);
  const catalogCacheRef = useRef<PromptSuggestionsCache>({});
  const loadRequestsRef = useRef<Map<string, PromptSuggestionsLoadRequest>>(new Map());
  const sessionDaysRef = useRef<Map<string, string>>(new Map());

  activeScopeRef.current = scope;

  const reload = useCallback(async (): Promise<void> => {
    const activeScope = activeScopeRef.current;
    if (!activeScope) {
      return;
    }

    const activeRequest = loadRequestsRef.current.get(activeScope.requestKey);
    if (activeRequest && !activeRequest.controller.signal.aborted) {
      return activeRequest.promise;
    }

    const cachedEntry = catalogCacheRef.current[activeScope.cacheKey];
    const controller = new AbortController();
    setLoadingRequestKey(activeScope.requestKey);
    setErrorCache((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[activeScope.cacheKey];
      return nextErrors;
    });

    const request = (async (): Promise<void> => {
      try {
        const result = await promptSuggestionsService.getSuggestions(
          activeScope.sessionId,
          activeScope.locale,
          {
            etag: cachedEntry?.etag || undefined,
            signal: controller.signal,
          },
        );
        if (controller.signal.aborted || activeScopeRef.current?.key !== activeScope.key) {
          return;
        }

        if (result.notModified) {
          if (!cachedEntry) {
            throw new Error('Coach suggestions returned an unexpected cache response.');
          }
          if (result.etag && result.etag !== cachedEntry.etag) {
            const nextCache = {
              ...catalogCacheRef.current,
              [activeScope.cacheKey]: {
                ...cachedEntry,
                etag: result.etag,
              },
            };
            catalogCacheRef.current = nextCache;
            setCatalogCache(nextCache);
          }
          return;
        }

        if (!result.catalog) {
          throw new Error('Coach suggestions did not return catalog content.');
        }

        const nextCache = {
          ...catalogCacheRef.current,
          [activeScope.cacheKey]: {
            catalog: result.catalog,
            etag: result.etag,
          },
        };
        catalogCacheRef.current = nextCache;
        setCatalogCache(nextCache);
      } catch (error) {
        if (isAbortError(error) || controller.signal.aborted) {
          return;
        }
        if (activeScopeRef.current?.key === activeScope.key) {
          const errorInfo = toErrorInfo(error);
          setErrorCache((currentErrors) => ({
            ...currentErrors,
            [activeScope.cacheKey]: errorInfo,
          }));
        }
        throw error;
      } finally {
        if (loadRequestsRef.current.get(activeScope.requestKey)?.controller === controller) {
          loadRequestsRef.current.delete(activeScope.requestKey);
        }
        if (activeScopeRef.current?.key === activeScope.key) {
          setLoadingRequestKey((currentRequestKey) => (
            currentRequestKey === activeScope.requestKey ? null : currentRequestKey
          ));
        }
      }
    })();

    loadRequestsRef.current.set(activeScope.requestKey, { controller, promise: request });
    return request;
  }, []);

  useEffect(() => {
    if (sessionId) {
      return;
    }

    for (const request of loadRequestsRef.current.values()) {
      request.controller.abort();
    }
    loadRequestsRef.current.clear();
    sessionDaysRef.current.clear();
    catalogCacheRef.current = {};
    setCatalogCache({});
    setErrorCache({});
    setLoadingRequestKey(null);
  }, [sessionId]);

  useEffect(() => {
    const requestScope = activeScopeRef.current;
    if (!requestScope) {
      return;
    }

    void reload().catch(() => undefined);
    return () => {
      const request = loadRequestsRef.current.get(requestScope.requestKey);
      request?.controller.abort();
    };
  }, [reload, scopeKey]);

  const activeCatalogEntry = scope ? catalogCache[scope.cacheKey] : undefined;
  const activeCatalog = activeCatalogEntry?.catalog || null;
  const activeError = scope ? errorCache[scope.cacheKey] || null : null;
  const isLoading = Boolean(scope && loadingRequestKey === scope.requestKey);
  const sessionDay = sessionId
    ? sessionDaysRef.current.get(sessionId) || getCurrentDay()
    : null;

  if (sessionId && sessionDay && !sessionDaysRef.current.has(sessionId)) {
    sessionDaysRef.current.set(sessionId, sessionDay);
  }

  const suggestionsBySurface = useMemo(
    () => activeCatalog && sessionId && sessionDay
      ? buildSuggestionsBySurface(activeCatalog, sessionId, sessionDay)
      : {},
    [activeCatalog, sessionDay, sessionId],
  );
  const status: PromptSuggestionsStatus = !scope
    ? 'idle'
    : activeCatalog
      ? activeCatalog.suggestions.length > 0 ? 'ready' : 'empty'
      : isLoading
        ? 'loading'
        : activeError
          ? 'error'
          : 'idle';
  const contextValue = useMemo<PromptSuggestionsContextValue>(() => ({
    catalogVersion: activeCatalog?.catalog_version || null,
    categories: activeCatalog?.categories || [],
    contentDigest: activeCatalog?.content_digest || null,
    suggestionsForSurface: (surface) => suggestionsBySurface[surface] || [],
    isLoading,
    status,
    error: activeError,
    reload,
  }), [activeCatalog, activeError, isLoading, reload, status, suggestionsBySurface]);

  return (
    <PromptSuggestionsContext.Provider value={contextValue}>
      {children}
    </PromptSuggestionsContext.Provider>
  );
};

export const usePromptSuggestions = (): PromptSuggestionsContextValue => {
  const context = useContext(PromptSuggestionsContext);
  if (!context) {
    throw new Error('usePromptSuggestions must be used within PromptSuggestionsProvider');
  }
  return context;
};